/**
 * GMA-1625 (CompassCare Web 10/14 — INTEL) — pure intel model.
 *
 * Caregiver intelligence & environmental awareness (NOT law-enforcement OSINT).
 * Provides the watch-type catalog with behavior & wellness triggers, and the
 * AC's core rule "every watch maps to concrete impacted clients" — resolved
 * geographically from a watch zone (center + radius) × client locations.
 *
 * All EXPORTED functions fail safe (internal helpers like distanceKm assume the exported callers validated their inputs): bad input yields a benign result, never a throw.
 */
import { homeKey } from "./clientsModel";

// Watch types with their behavior & wellness triggers (why this matters for a
// caregiver). Vocabulary is part of the spec — caregiver-centric, not OSINT.
export const WATCH_TYPES = Object.freeze({
  heat: Object.freeze({
    label: "Heat advisory",
    category: "weather",
    triggers: Object.freeze(["dehydration", "agitation", "seizure threshold"]),
  }),
  cold: Object.freeze({
    label: "Cold advisory",
    category: "weather",
    triggers: Object.freeze(["hypothermia risk", "circulation", "fall risk on ice"]),
  }),
  storm: Object.freeze({
    label: "Storm",
    category: "weather",
    triggers: Object.freeze(["anxiety", "power loss", "route access delay"]),
  }),
  flood: Object.freeze({
    label: "Flood",
    category: "weather",
    triggers: Object.freeze(["evacuation", "route access delay", "isolation"]),
  }),
  air_quality: Object.freeze({
    label: "Air quality / smoke",
    category: "environmental",
    triggers: Object.freeze(["respiratory distress", "reduced outdoor time"]),
  }),
  power_outage: Object.freeze({
    label: "Power outage",
    category: "utility",
    triggers: Object.freeze(["device coverage loss", "missed routines", "refrigerated meds"]),
  }),
  internet_outage: Object.freeze({
    label: "Internet outage",
    category: "utility",
    triggers: Object.freeze(["device coverage loss", "monitoring gap"]),
  }),
  fireworks: Object.freeze({
    label: "Fireworks / crowds / noise",
    category: "community",
    triggers: Object.freeze(["sensory distress", "elopement", "sleep disruption"]),
  }),
  public_safety: Object.freeze({
    label: "Public-safety activity",
    category: "situational",
    triggers: Object.freeze(["route access delay", "shelter-in-place"]),
  }),
  // compass-map#31 / react-app#422 — clinical homes for CAP families that were
  // previously untyped on INTEL (FEMA DOB multi-hazard COP).
  seismic: Object.freeze({
    label: "Earthquake / tsunami",
    category: "geophysical",
    triggers: Object.freeze([
      "fall risk",
      "evacuation",
      "power loss",
      "medication access",
    ]),
  }),
  avalanche: Object.freeze({
    label: "Avalanche",
    category: "geophysical",
    triggers: Object.freeze(["isolation", "route access delay", "shelter"]),
  }),
  missing_person: Object.freeze({
    label: "Missing person / AMBER",
    category: "situational",
    // #422 / #759: Silver/AMBER/endangered-person alerts mean check OUR
    // roster first — the client who wanders may be the subject. Keep the
    // elopement/wandering clinical triggers; do not dilute with generic
    // "route delay" public-safety copy.
    triggers: Object.freeze([
      "client roster check",
      "wandering / elopement",
      "heightened awareness",
    ]),
  }),
  health_advisory: Object.freeze({
    label: "Health advisory",
    category: "health",
    triggers: Object.freeze(["infection risk", "medication adjustment"]),
  }),
});

const isNum = (v) => typeof v === "number" && Number.isFinite(v);
const toRad = (d) => (d * Math.PI) / 180;
const EARTH_KM = 6371;

// Great-circle distance in km between two {lat,lng} points.
function distanceKm(a, b) {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const raw =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  // Clamp to [0,1] — floating-point error can push `raw` just past 1, which
  // would make Math.sqrt(1 - raw) NaN and break the distance for antipodal-ish
  // points.
  const s = Math.min(1, Math.max(0, raw));
  return EARTH_KM * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

/**
 * The only safe way to read WATCH_TYPES by key.
 *
 * WATCH_TYPES is a plain object literal, so `WATCH_TYPES["constructor"]`
 * returns `Object` — truthy. Callers write their not-found fallback as
 * `WATCH_TYPES[key] ? … : fallback`, and a truthy result skips it: the
 * caller then reads `.triggers` off `Object` (undefined) and calls `.map`,
 * which throws and blanks the whole panel. Own-property lookup closes that.
 *
 * Also rejects a hit whose `triggers` is not an array, since every consumer
 * maps over it. Returns null — never a partial object — so one guard at the
 * call site covers both cases.
 *
 * @returns {object|null} the frozen type entry, or null when unmapped.
 */
export function watchTypeFor(key) {
  if (typeof key !== "string" || !key) return null;
  if (!Object.prototype.hasOwnProperty.call(WATCH_TYPES, key)) return null;
  const type = WATCH_TYPES[key];
  return type && Array.isArray(type.triggers) ? type : null;
}

/**
 * The clients concretely inside a watch's zone (center + radiusKm). Every watch
 * resolves to real clients — no abstract feeds (AC).
 * @param {{center:{lat,lng}, radiusKm:number}} watch
 * @param {Array<{id,lat,lng}>} clients
 * @returns {Array} impacted clients ([] on bad input)
 */
export function clientsInWatchZone(watch, clients) {
  const list = Array.isArray(clients) ? clients : [];
  const center = watch && watch.center;
  if (
    !center ||
    !isNum(center.lat) ||
    !isNum(center.lng) ||
    !isNum(watch.radiusKm)
  ) {
    return [];
  }
  return list.filter(
    (c) => c && isNum(c.lat) && isNum(c.lng) && distanceKm(center, c) <= watch.radiusKm,
  );
}

/**
 * Impact counts for a single watch: impacted clients, distinct group homes,
 * and independent-living clients.
 * @returns {{clientsImpacted:number, groupHomesImpacted:number, independentLivingImpacted:number}}
 */
export function summarizeWatchImpact(watch, clients) {
  const impacted = clientsInWatchZone(watch, clients);
  const homes = new Set();
  let independent = 0;
  for (const c of impacted) {
    // `livingEnv` is the codebase's field for group-home vs independent
    // (see clientsModel.js): "group_home" | "independent".
    if (c.livingEnv === "group_home") {
      // Use the SHARED home-keying helper so INTEL counts distinct homes
      // identically to the roster roll-up (no duplicated keying logic).
      homes.add(homeKey(c));
    } else if (c.livingEnv === "independent") {
      independent += 1;
    }
  }
  return {
    clientsImpacted: impacted.length,
    groupHomesImpacted: homes.size,
    independentLivingImpacted: independent,
  };
}
